import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Pencil, MapPin, ClipboardList, ScrollText } from "lucide-react";

const TABS = [
  { key: "coord", label: "Coordonnées", icon: MapPin },
  { key: "fiche", label: "Fiche praticien", icon: ClipboardList },
  { key: "seminaires", label: "Événements", icon: ClipboardList },
  { key: "journal", label: "Journal", icon: ScrollText },
];

const VALIDATION_LABELS = { BROUILLON: "Brouillon", EN_ATTENTE: "En attente", VALIDE: "Validé", REJETE: "Rejeté" };

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-cfhtb-text/70 font-medium">{label}</p>
      <p className="text-sm text-cfhtb-heading mt-0.5">{value || "—"}</p>
    </div>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function MemberDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("coord");
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/admin/membres/${id}`).then(setData).catch((e) => setError(e.message));
  }, [id]);

  if (error) {
    return <AppLayout variant="admin"><p className="text-cfhtb-error text-sm">{error}</p></AppLayout>;
  }
  if (!data) {
    return (
      <AppLayout variant="admin">
        <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
      </AppLayout>
    );
  }

  const { person, practitioner_profile: fiche, inscriptions, journal, institut } = data;

  return (
    <AppLayout variant="admin">
      <div data-testid="member-detail-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">
              {person.civilite} {person.prenom} {person.nom || ""}
            </h1>
            <p className="text-sm text-cfhtb-text mt-1">{person.email_principal}</p>
          </div>
          <Link
            to={`/admin/membres/${id}/modifier`}
            data-testid="edit-member-button"
            className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
          >
            <Pencil className="h-4 w-4" /> Modifier
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-200 overflow-x-auto" role="tablist">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                role="tab"
                data-testid={`tab-${key}`}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === key
                    ? "border-cfhtb-primary text-cfhtb-primary"
                    : "border-transparent text-cfhtb-text hover:text-cfhtb-heading"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === "coord" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-content-coord">
                <Field label="Type" value={person.type === "PRATICIEN" ? "Praticien" : "Visiteur"} />
                <Field label="Statut" value={person.statut} />
                <Field label="Téléphone" value={person.telephone} />
                <Field label="Profession" value={person.statut_pro} />
                <Field label="N° RPPS/ADELI" value={person.rpps_adeli} />
                <Field label="Institut" value={institut?.nom} />
                <Field label="Date d'anniversaire" value={formatDate(person.date_naissance)} />
                <Field label="Date d'adhésion" value={formatDate(person.date_adhesion)} />
                <Field label="Fin d'adhésion" value={formatDate(person.date_fin_adhesion)} />
                <Field label="Source" value={person.source} />
                <Field label="Consentement annuaire" value={person.consentement_annuaire ? "Oui" : "Non"} />
                <Field label="Consentement newsletter" value={person.consentement_newsletter ? "Oui" : "Non"} />
                <Field label="Créé le" value={formatDate(person.created_at)} />
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-xs uppercase tracking-wider text-cfhtb-text/70 font-medium">Tags</p>
                  <div className="flex flex-wrap gap-2 mt-1.5" data-testid="member-tags-display">
                    {(person.tags_details || []).length === 0 ? (
                      <span className="text-sm text-cfhtb-text">—</span>
                    ) : (
                      person.tags_details.map((t) => (
                        <span key={t.nom} className="text-xs px-2.5 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: t.couleur }}>
                          {t.nom}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "fiche" && (
              <div data-testid="tab-content-fiche">
                {!fiche ? (
                  <p className="text-sm text-cfhtb-text">Ce membre n'a pas de fiche praticien.</p>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${
                        fiche.statut_validation === "VALIDE"
                          ? "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30"
                          : "bg-cfhtb-warning/10 text-cfhtb-warning border-cfhtb-warning/30"
                      }`}>
                        {VALIDATION_LABELS[fiche.statut_validation] || fiche.statut_validation}
                      </span>
                      {fiche.visible_sur_carte && (
                        <span className="text-xs text-cfhtb-secondary">Visible sur la carte</span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      <Field label="Cabinet" value={fiche.cabinet_nom} />
                      <Field label="Adresse" value={fiche.adresse} />
                      <Field label="Ville" value={`${fiche.code_postal || ""} ${fiche.ville || ""}`.trim()} />
                      <Field label="Pays" value={fiche.pays} />
                      <Field label="Spécialités" value={(fiche.specialites || []).join(", ")} />
                      <Field label="Publics" value={(fiche.publics || []).join(", ")} />
                      <Field label="Téléconsultation" value={fiche.teleconsultation ? "Oui" : "Non"} />
                    </div>
                    {fiche.presentation && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-cfhtb-text/70 font-medium">Présentation</p>
                        <p className="text-sm text-cfhtb-text mt-1 leading-relaxed">{fiche.presentation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "seminaires" && (
              <div data-testid="tab-content-seminaires">
                {inscriptions.length === 0 ? (
                  <p className="text-sm text-cfhtb-text">Aucune inscription à un événement.</p>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[#F7F7F7] border-b border-slate-200">
                      <tr>
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-3 py-2">Événement</th>
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-3 py-2">Date</th>
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-3 py-2">Source</th>
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-3 py-2">Présent</th>
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-3 py-2">Replay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inscriptions.map((e) => (
                        <tr key={e.id} className="border-b border-slate-100 text-sm text-cfhtb-text">
                          <td className="px-3 py-2.5 font-medium text-cfhtb-heading">{e.seminar?.titre || e.seminar_id}</td>
                          <td className="px-3 py-2.5">{formatDate(e.seminar?.date_debut)}</td>
                          <td className="px-3 py-2.5">{e.source}</td>
                          <td className="px-3 py-2.5">{e.present ? "Oui" : "Non"}</td>
                          <td className="px-3 py-2.5">{e.acces_replay ? "Oui" : "Non"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === "journal" && (
              <div data-testid="tab-content-journal">
                {journal.length === 0 ? (
                  <p className="text-sm text-cfhtb-text">Aucune action enregistrée pour ce membre.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {journal.map((l) => (
                      <li key={l.id} className="py-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-cfhtb-heading">{l.action}</p>
                          <p className="text-xs text-cfhtb-text">Acteur : {l.acteur_id || "système"} · IP : {l.ip || "—"}</p>
                        </div>
                        <span className="text-xs text-cfhtb-text shrink-0">
                          {l.date ? new Date(l.date).toLocaleString("fr-FR") : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
